import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { menuService, menuTypeService } from '../../utils/api';
import ConfirmationModal from '../ConfirmationModal';
import { useSchool } from '../../contexts/SchoolContext';

// Helper function to format dates (moved outside the component)
const formatDate = (date) => {
  const options = { month: 'short', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

const CopyWeekModal = ({ 
  isOpen, 
  onClose, 
  sourceWeekDays, // Array of { dayNumber, date } for source (current) week
  sourceMenuId,
  sourceMenuTypeId,
  onCopyComplete 
}) => {
  const { t, i18n } = useTranslation();
  const { school } = useSchool();
  
  // State for form values
  const [targetMenuTypeId, setTargetMenuTypeId] = useState('');
  const [targetMonth, setTargetMonth] = useState('');
  const [targetWeek, setTargetWeek] = useState('');
  
  // State for data loading and UI
  const [menuTypes, setMenuTypes] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  
  // Get current date for default month value and reset state when modal opens
  useEffect(() => {
    if (!isOpen) return; // Only run when modal is opening
    
    console.log('[CopyWeekModal] Modal opening - resetting all state');
    
    // Reset ALL state when modal opens
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    setTargetMonth(`${year}-${month}`);
    
    // Always reset to source menu type as default when opening
    setTargetMenuTypeId(sourceMenuTypeId || '');
    
    // Reset ALL modal state
    setTargetWeek('');
    setAvailableWeeks([]);
    setError(null);
    setShowConfirmationModal(false);
    setConfirmationData(null);
    
    console.log('[CopyWeekModal] State reset completed');
  }, [isOpen, sourceMenuTypeId]); // Removed targetMenuTypeId dependency to allow proper reset
  
  // Cleanup state when modal closes
  useEffect(() => {
    if (!isOpen) {
      console.log('[CopyWeekModal] Modal closing - cleaning up state');
      // Reset all state to initial values when modal closes
      setTargetMenuTypeId('');
      setTargetMonth('');
      setTargetWeek('');
      setAvailableWeeks([]);
      setLoading(false);
      setSubmitting(false);
      setError(null);
      setShowConfirmationModal(false);
      setConfirmationData(null);
    }
  }, [isOpen]);
  
  // Fetch menu types when the modal opens
  useEffect(() => {
    if (!isOpen || !school?.id) return;
    
    const fetchMenuTypes = async () => {
      try {
        setLoading(true);
        const menuTypesData = await menuTypeService.getMenuTypes(school.id, i18n.language || 'en');
        setMenuTypes(menuTypesData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching menu types:', err);
        setError(t('errors.failedToLoadMenuTypes'));
        setLoading(false);
      }
    };
    
    fetchMenuTypes();
  }, [isOpen, school?.id, t, i18n.language]);
  
  // Generate available weeks when target month changes
  useEffect(() => {
    if (!targetMonth) {
      setAvailableWeeks([]);
      return;
    }
    
    const [year, month] = targetMonth.split('-').map(n => parseInt(n, 10)); // month is 1-12
    const firstDayOfMonth = new Date(year, month - 1, 1); // month - 1 for 0-indexed JS months
    
    let currentWeekStart = new Date(firstDayOfMonth);
    // Correctly adjust to the Monday of the week containing the first day of the month.
    // getDay() returns 0 for Sunday, 1 for Monday, ..., 6 for Saturday.
    // We want to subtract (dayOfWeek - 1) if not Sunday, or 6 if Sunday.
    const dayOfWeek = currentWeekStart.getDay();
    currentWeekStart.setDate(currentWeekStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    const daysInMonth = new Date(year, month, 0).getDate(); // Days in target month (month is 1-12 here for Date constructor)
    const lastDayOfTargetMonth = new Date(year, month - 1, daysInMonth); // month - 1 for 0-indexed JS months
    
    const weeks = [];
    let safetyIterations = 0;

    // Loop while the start of the week (Monday) is on or before the last day of the target month,
    // OR if the week starts in the previous month but might contain days from the target month.
    // A week should be considered if its Monday is in the month before, the current month, or the month after (if it contains days from current month)
    // More robust: iterate as long as the current week could potentially overlap.
    while (safetyIterations < 7) { // Limit to 7 weeks to prevent infinite loops, covers all month overlaps
      safetyIterations++;

      const weekEndCandidate = new Date(currentWeekStart);
      weekEndCandidate.setDate(currentWeekStart.getDate() + 4); // Potential Friday

      // Determine if this week (Monday to Friday) has any overlap with the target month.
      let hasDayInTargetMonth = false;
      const weekDays = [];
      let firstDayObjectForLabel = null;
      let lastDayObjectForLabel = null;

      for (let i = 0; i < 5; i++) { // Iterate Monday to Friday
        const dayDate = new Date(currentWeekStart);
        dayDate.setDate(currentWeekStart.getDate() + i);

        if (i === 0) firstDayObjectForLabel = dayDate;
        if (i === 4) lastDayObjectForLabel = dayDate;

        const isThisDayInTargetMonth = dayDate.getFullYear() === year && dayDate.getMonth() === (month - 1);
        if (isThisDayInTargetMonth) {
          hasDayInTargetMonth = true;
        }
        
        // Construct the date string from local components to avoid timezone shifts
        const localDateString = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
        
        weekDays.push({
          dayNumber: dayDate.getDate(),
          date: localDateString, // Use the correctly formatted local date string
          inTargetMonth: isThisDayInTargetMonth
        });
      }

      if (hasDayInTargetMonth) {
        console.log('[CopyWeekModal] Adding week. currentWeekStart:', currentWeekStart.toISOString(), 'Day:', currentWeekStart.getDay());
        weeks.push({
          startDate: weekDays[0].date,
          endDate: weekDays[4].date,
          label: `${formatDate(firstDayObjectForLabel)} - ${formatDate(lastDayObjectForLabel)}`,
          days: weekDays
        });
      } else {
        // If no days in this week are in the target month, AND the week has started *after* the target month, we can stop.
        if (currentWeekStart.getFullYear() > year || (currentWeekStart.getFullYear() === year && currentWeekStart.getMonth() > (month - 1))) {
          console.log('[CopyWeekModal] Stopping week generation. currentWeekStart is past target month and no overlap.', currentWeekStart.toISOString());
          break;
        }
      }
      
      // Advance to the next Monday
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      // If the next Monday is already too far into the next month, break early (e.g. 2nd week of next month)
      if (currentWeekStart.getFullYear() === year && currentWeekStart.getMonth() > month) { // Already two months ahead
          break;
      }
      if (currentWeekStart.getFullYear() > year && currentWeekStart.getMonth() > 0 ) { // Next year, and past Jan
          break;
      }
    }
    
    setAvailableWeeks(weeks);
    setTargetWeek('');
  }, [targetMonth]); // Removed formatDate from dependency array as it's now stable (outside component)
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!targetWeek) {
      toast.error(t('errors.selectWeekFirst', 'Please select a week first'));
      return;
    }
    
    try {
      console.log('[CopyWeek] Starting form submission check');
      setSubmitting(true);
      
      // Find the selected week object
      const selectedWeekData = availableWeeks.find(
        week => `${week.startDate}_${week.endDate}` === targetWeek
      );
      
      if (!selectedWeekData) {
        throw new Error('Selected week not found');
      }
      
      // First check if a menu already exists for the target month and type
      const existingMenu = await menuService.checkExistingMenu(school?.id, targetMonth, targetMenuTypeId);
      
      // If a menu exists, show confirmation modal
      if (existingMenu) {
        console.log('[CopyWeek] Found existing menu, showing confirmation modal:', existingMenu);
        setConfirmationData({
          targetMenuTypeId,
          targetMonth,
          targetWeek
        });
        setShowConfirmationModal(true);
        setSubmitting(false);
        return;
      }
      
      // If no existing menu, proceed with copy
      console.log('[CopyWeek] No existing menu found, proceeding with copy');
      console.log('[CopyWeek] Selected week data:', selectedWeekData);
      
      // Gather data for API request
      const data = {
        sourceSchoolId: school?.id,
        sourceMenuTypeId,
        sourceWeekDays,
        targetMenuTypeId,
        targetMonth,
        targetWeekDays: selectedWeekData.days
      };
      
      console.log('[CopyWeek] Submitting request with data:', JSON.stringify(data, null, 2));
      
      // Call the API
      console.log('[CopyWeek] Calling menuService.copyWeek()');
      const response = await menuService.copyWeek(data);
      
      console.log('[CopyWeek] API Response received:', response);
      console.log('[CopyWeek] Response menuId:', response.menuId);
      console.log('[CopyWeek] Response menu data structure:', response.menu ? {
        id: response.menu.id,
        days: response.menu.days ? `${response.menu.days.length} days` : 'No days',
        days_sample: response.menu.days ? response.menu.days.slice(0, 2) : 'N/A',
        day_structure: response.menu.days && response.menu.days.length > 0 ? 
          Object.keys(response.menu.days[0]) : 'N/A'
      } : 'No menu data');
      
      // Pass both the target menu ID and the full menu data to the callback
      // The new response format includes menu data in response.menu
      console.log('[CopyWeek] Calling onCopyComplete with menuId and menu data');
      
      // Ensure we have the proper data structure no matter what the API returns
      let processedMenuData = response.menu;
      
      // If there's no proper menu object but we have days directly, create a wrapper object
      if (!processedMenuData && Array.isArray(response.days)) {
        console.log('[CopyWeek] Creating menu wrapper for days array');
        processedMenuData = {
          id: response.menuId,
          days: response.days
        };
      }
      
      // If there's no proper menu object or days, use the entire response as the menu data
      if (!processedMenuData) {
        console.log('[CopyWeek] Using entire response as menu data');
        processedMenuData = response;
      }
      
      console.log('[CopyWeek] Final processed menu data to pass:', processedMenuData);
      onCopyComplete(response.menuId, processedMenuData);
      toast.success(t('copyWeek.success', 'Week copied successfully!'));
      onClose(); // Close modal on success
      
    } catch (error) {
      console.error('[CopyWeek] Error:', error);
      toast.error(error.message || t('errors.failedToCopyWeek', 'Failed to copy week'));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle confirmation for overwriting existing menu
  const handleConfirmOverwrite = async () => {
    setShowConfirmationModal(false);
    if (!confirmationData) {
      toast.error(t('errors.missingConfirmationData', 'Missing confirmation data.'));
      return;
    }

    // Find the selected week object from confirmationData
    const selectedWeekData = availableWeeks.find(
      week => `${week.startDate}_${week.endDate}` === confirmationData.targetWeek // Assuming targetWeek is stored in confirmationData
    );

    if (!selectedWeekData) {
      toast.error(t('errors.selectedWeekNotFound', 'Selected week data not found in confirmation.'));
      return;
    }

    try {
      console.log('[CopyWeek] Starting confirmed form submission');
      setSubmitting(true);

      const data = {
        sourceSchoolId: school?.id,
        sourceMenuTypeId,      // from component props
        sourceWeekDays,        // from component props
        targetMenuTypeId: confirmationData.targetMenuTypeId, // from confirmationData
        targetMonth: confirmationData.targetMonth,          // from confirmationData
        targetWeekDays: selectedWeekData.days
      };

      console.log('[CopyWeek] Submitting confirmed request with data:', JSON.stringify(data, null, 2));

      const response = await menuService.copyWeek(data);

      console.log('[CopyWeek] Confirmed API Response received:', response);

      let processedMenuData = response.menu;
      if (!processedMenuData && Array.isArray(response.days)) {
        processedMenuData = { id: response.menuId, days: response.days };
      }
      if (!processedMenuData) {
        processedMenuData = response;
      }

      onCopyComplete(response.menuId, processedMenuData);
      toast.success(t('copyWeek.success', 'Week copied successfully!'));
      onClose(); // Close modal on success

    } catch (error) {
      console.error('[CopyWeek] Error in confirmed copy:', error);
      toast.error(error.message || t('errors.failedToCopyWeek', 'Failed to copy week'));
    } finally {
      setSubmitting(false);
    }
  };
  
  // If the modal is not open, don't render anything
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-gray-500 bg-opacity-75 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium">{t('copyWeek.title', 'Copy Week')}</h3>
          <button
            type="button"
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            <span className="sr-only">{t('common.close')}</span>
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('copyWeek.targetMenuType', 'Target Menu Type')}
            </label>
            <select
              value={targetMenuTypeId}
              onChange={(e) => setTargetMenuTypeId(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              disabled={loading || submitting}
              required
            >
              <option value="">{t('copyWeek.selectMenuType', 'Select Menu Type')}</option>
              {menuTypes.map((menuType) => (
                <option key={menuType.id} value={menuType.id}>
                  {menuType.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('copyWeek.targetMonth', 'Target Month')}
            </label>
            <input
              type="month"
              value={targetMonth}
              onChange={(e) => setTargetMonth(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              disabled={loading || submitting}
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('copyWeek.targetWeek', 'Target Week')}
            </label>
            <select
              value={targetWeek}
              onChange={(e) => setTargetWeek(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              disabled={loading || submitting || !targetMonth || availableWeeks.length === 0}
              required
            >
              <option value="">{t('copyWeek.selectWeek', 'Select Week')}</option>
              {availableWeeks.map((week, index) => (
                <option key={`${week.startDate}_${week.endDate}`} value={`${week.startDate}_${week.endDate}`}>
                  {t('copyWeek.weekLabel', 'Week {{weekNumber}}: {{dateRange}}', {
                    weekNumber: index + 1,
                    dateRange: week.label
                  })}
                </option>
              ))}
            </select>
          </div>
          
          {targetWeek && (
            <div className="mb-4 p-2 bg-gray-100 rounded text-sm">
              <p className="font-medium">{t('copyWeek.daysInTargetMonth', 'Days in target month:')}</p>
              <ul className="list-disc pl-5 mt-1">
                {availableWeeks
                  .find(week => `${week.startDate}_${week.endDate}` === targetWeek)?.days
                  .filter(day => day.inTargetMonth)
                  .map(day => {
                    // Parse the YYYY-MM-DD string into components to create a local Date object
                    const [year, month, dayOfMonth] = day.date.split('-').map(Number);
                    const localDate = new Date(year, month - 1, dayOfMonth); // month - 1 for 0-indexed JS months
                    return (
                      <li key={day.date}>
                        {localDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}
          
          <div className="flex justify-end pt-2">
            <button
              type="button"
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
              onClick={onClose}
              disabled={loading || submitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              disabled={loading || submitting || !targetMenuTypeId || !targetMonth || !targetWeek}
            >
              {loading || submitting ? t('common.loading') : t('copyWeek.copy', 'Copy')}
            </button>
          </div>
        </form>
      </div>
      
      {/* Confirmation Modal for Overwriting Existing Menu */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmOverwrite}
        title={t('copyWeek.confirmOverwrite', 'Confirm Overwrite')}
        message={t('copyWeek.confirmOverwriteMessage', 'A menu already exists for the target month and menu type. Do you want to overwrite the menu items for the selected days?')}
      />
    </div>
  );
};

export default CopyWeekModal; 